import {observer} from 'mobx-react';
import {useContext, useEffect, useState, useMemo} from 'react';
import {NetworkContext} from '../lib/context';
import './roster-comparison-summary.scss';
import {
    Table,
    TableHead,
    TableRow,
    TableHeader,
    TableBody,
    TableCell,
    TableContainer,
    DataTable,
    TableToolbar,
    TableToolbarContent,
    IconButton,
    DataTableSkeleton,
    InlineNotification,
} from '@carbon/react';
import {Car, CheckmarkFilled, CloseFilled, Time} from '@carbon/icons-react';
import getFormatter from '../../../lib/formatter';
import CustomPagination from '../../../components/custom-pagination/CustomPagination';

const PAGE_SIZE = 15;

const RosterComparisonSummary = observer(() => {
    const networkStore = useContext(NetworkContext);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [activeMetric, setActiveMetric] = useState<'time' | 'distance'>('time');
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(PAGE_SIZE);

    // Use the selected rosters from the network store
    const selectedRosterIds = useMemo(() => networkStore.selectedRosters || [], [networkStore.selectedRosters]);

    useEffect(() => {
        const controller = new AbortController();
        let isActive = true;
        const loadData = async () => {
            setError(null);
            try {
                if (isActive) setLoading(true);
                await networkStore.loadNetworkAdequacyComparison(controller);
            } catch (err: any) {
                if (!(err && err.name === 'AbortError')) {
                    setError('Failed to load network adequacy comparison');
                }
            } finally {
                if (isActive)setLoading(false);
            }
        };
        if (networkStore.selectedRosters.length >= 2) {
            loadData();
        }
        return () => {
            isActive = false;
            controller.abort();
        };
    }, [networkStore.geographyFilters, networkStore.specialtyFilter, networkStore.selectedRosters]);

    useEffect(() => {
        const controller = new AbortController();
        const loadData = async () => {
            try {
                await networkStore.loadProviderRosters(controller);
            } catch (err) {
                console.error(err);
            }
        };
        if (networkStore.providerRosters.length === 0) {
            loadData();
        }
        return () => controller.abort();
    }, [networkStore.appliedGeographyFilter, networkStore.selectedRosters]);

    const getRosterName = (rosterId: string, revisionId: string) => {
        if (Array.isArray(networkStore.providerRosters) && networkStore.providerRosters.length > 0) {
            const roster = networkStore.providerRosters.find(r => r.rosterId === rosterId && r.revisionId === revisionId);
            return `${roster?.revisionName} | ${roster?.rosterName}` || `Unknown Roster (${rosterId})`;
        }
        return `Unknown Roster (${rosterId})`;
    };

    const ComplianceIcon = ({isCompliant}: {isCompliant: boolean}) => {
        return isCompliant ? (
            <CheckmarkFilled className='text-green-600' size={16} data-testid="checkmark-icon" />
        ) : (
            <CloseFilled className='text-red-600' size={16} data-testid="close-icon" />
        );
    };

    // Only show data if at least 2 rosters are selected
    const showComparison = selectedRosterIds.length >= 2;
    const displayRosters = selectedRosterIds.slice(0, 5); // Limit to 5 rosters max

    const headers = useMemo(() => [
        {key: 'specialty', header: 'Specialty'},
        ...displayRosters.map((r) => ({
            key: `${r.rosterId}-${r.revisionId}`,
            header: getRosterName(r.rosterId, r.revisionId),
        })),
        {key: 'complianceTarget', header: 'Compliance Target'},
    ], [displayRosters, networkStore.providerRosters]);

    const rows = useMemo(() => {
        if (!showComparison) return [];
        const rawData = networkStore.rosterComparisonNetworkAdequacyData || [];
        const start = page * pageSize;
        const end = start + pageSize;
        return rawData.slice(start, end)?.map((item: any, idx: number) => {
            // Build dynamic roster columns
            const rosterColumns = displayRosters.reduce((acc, r, index) => {
                // Determine keys for compliance and percentage
                const flagKey = activeMetric === 'time'
                    ? `timeAdequacyFlag${index + 1}`
                    : `distanceAdequacyFlag${index + 1}`;
                const percentKey = activeMetric === 'time'
                    ? `populationMetTime${index + 1}`
                    : `populationMetDistance${index + 1}`;

                // Format percentage value
                const percentValue = getFormatter('percentfloor', .1).format(item[percentKey]);

                acc[`${r.rosterId}-${r.revisionId}`] = (
                    <span className="flex items-center gap-2">
                        <ComplianceIcon isCompliant={item[flagKey] === '1' || item[flagKey] === 1} />
                        <span className="text-xs text-gray-700">{percentValue}</span>
                    </span>
                );
                return acc;
            }, {} as Record<string, JSX.Element>);

            // Compliance target value
            const complianceTarget = activeMetric === 'time'
                ? item.timeComplianceTarget
                : item.distanceComplianceTarget;

            return {
                id: `${idx}`,
                specialty: item.specialtyName,
                complianceTarget: getFormatter('percentfloor', .1).format(complianceTarget),
                ...rosterColumns
            };
        }) || [];
    }, [displayRosters, networkStore.rosterComparisonNetworkAdequacyData, activeMetric, showComparison, page, pageSize]);
    
    useEffect(() => {
        const controller = new AbortController();
        const loadData = async () => {
            try {
                await networkStore.loadSpecialtiesData(controller);
            } catch (err) {
                console.error('Failed to load estimated compliance summary data. Please try again later.');
            }
        };

        loadData();
        return () => controller.abort();
    }, [networkStore.appliedGeographyFilter,
        networkStore.runId,
        networkStore.revisionId,
        networkStore.distanceCalcType,
        networkStore.readyToLoadData()]);

    return (
        <div className='flex flex-1 bg-background-gray-100 relative' id='roster-comparison-table'>
            {/* Main Content Area */}
            <div className='flex-1 p-6'>
                <div className='grid grid-cols-12 gap-6'>
                    {/* Comparison Summary */}
                    <div className='col-span-3 mt-8'>
                        <div className='bg-gray-50 p-4 h-full'>
                            <h4 className='text-lg font-semibold mb-4' data-testid='comparison-summary-title'>Comparison Summary</h4>
                            {error ? (
                                <div className='text-sm text-gray-500'>
                                    <p>Unable to load comparison data due to an error.</p>
                                </div>
                            ) : showComparison ? (
                                <div className='space-y-3 text-sm' data-testid='comparison-summary-details'>
                                    <div>
                                        • {displayRosters.length} rosters selected for comparison
                                    </div>
                                    <div>• 12 differences found</div>
                                    <div>
                                        • 12 cardiology counties where Roster A is compliant and
                                        Roster B is not
                                    </div>
                                    <div>• Average provider cost 8% higher in Roster B</div>
                                    <div>
                                        • 4 dermatology counties where Roster B is compliant and
                                        Roster A is not
                                    </div>
                                </div>
                            ) : (
                                <div className='text-sm text-gray-500' data-testid='comparison-summary-empty'>
                                    <p>Select at least 2 rosters to see comparison metrics.</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Comparison Table */}
                    <div className='col-span-8'>
                        {showComparison ? (
                            <>
                                {error ? (
                                    <div className='bg-white p-8 rounded-lg shadow-sm flex items-center justify-center h-64 mt-8'>
                                        <div className='w-full max-w-md'>
                                            <InlineNotification
                                                kind="error"
                                                title="Error Loading Data"
                                                subtitle={error}
                                                hideCloseButton={false}
                                                onCloseButtonClick={() => setError(null)}
                                            />
                                        </div>
                                    </div>
                                ) : loading ? (
                                    <DataTableSkeleton
                                        headers={headers}
                                        showHeader={false}
                                        rowCount={9}
                                        columnCount={headers.length}
                                    />
                                ) : (
                                    <TableContainer data-testid='comparison-table-container'>
                                        <TableToolbar size='sm'>
                                            <TableToolbarContent>
                                                <IconButton
                                                    size='sm'
                                                    label='Time Metrics'
                                                    className={`!bg-[#e0e0e0] ${activeMetric === 'time' ? '!bg-white !border !border-blue-500' : ''}`}
                                                    onClick={() => setActiveMetric('time')}
                                                >
                                                    <Time color={'black'} />
                                                </IconButton>
                                                <span className='ml-[1px]'>
                                                    <IconButton
                                                        size='sm'
                                                        label='Distance Metrics'
                                                        className={`!bg-[#e0e0e0] ${activeMetric === 'distance' ? '!bg-white !border !border-blue-500' : ''}`}
                                                        onClick={() => setActiveMetric('distance')}
                                                    >
                                                        <Car color={'black'} />
                                                    </IconButton>
                                                </span>
                                            </TableToolbarContent>
                                        </TableToolbar>
                                        <DataTable rows={rows} headers={headers} size='lg'>
                                            {({
                                                rows,
                                                headers,
                                                getHeaderProps,
                                                getRowProps,
                                                getTableProps,
                                            }) => (
                                                <>
                                                    <Table {...getTableProps()} className='min-w-full bg-white'>
                                                        <TableHead>
                                                            <TableRow className='sticky table top-0 bg-white z-10'>
                                                                {headers.map((header) => (
                                                                    <TableHeader
                                                                        {...getHeaderProps({header})}
                                                                        key={header.key}
                                                                        className='bg-white z-20 border-b border-gray-300 text-left p-2'
                                                                        style={{width: `${header.key === 'specialty' ? '40%' : `${60 / (headers.length - 1)}%`}`}}
                                                                    >
                                                                        {header.header}
                                                                    </TableHeader>
                                                                ))}
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody className='block max-h-[60vh] overflow-y-auto'>
                                                            {rows.length === 0 ? (
                                                                <TableRow className="table w-full">
                                                                    <TableCell
                                                                        colSpan={headers.length}
                                                                        className="p-3"
                                                                        style={{textAlign: 'center', fontSize: '1rem', fontWeight: '500'}}
                                                                    >
                                                                        No Data Available
                                                                    </TableCell>
                                                                </TableRow>
                                                            ) : (
                                                                rows.map((row) => (
                                                                    <TableRow
                                                                        {...getRowProps({row})}
                                                                        key={row.id}
                                                                        className='table w-full'
                                                                    >
                                                                        {row.cells.map((cell) => (
                                                                            <TableCell
                                                                                key={cell.id}
                                                                                className='p-2'
                                                                                style={{width: `${cell.info.header === 'specialty' ? '40%' : `${60 / (headers.length - 1)}%`}`}}>
                                                                                {cell.value}
                                                                            </TableCell>
                                                                        ))}
                                                                    </TableRow>
                                                                ))
                                                            )}
                                                        </TableBody>
                                                    </Table>
                                                    {!loading && !error && rows.length > 0 && (
                                                        <CustomPagination
                                                            totalItems={networkStore.rosterComparisonNetworkAdequacyData?.length || 0}
                                                            pageSize={pageSize}
                                                            page={page}
                                                            pageSelectorType='Input'
                                                            onPageSizeChange={(newPageSize: number) => {
                                                                setPageSize(newPageSize);
                                                            }}
                                                            onPageChange={(newPage: number) => {
                                                                setPage(newPage);
                                                            }} 
                                                        />
                                                    )}
                                                </>
                                            )}
                                        </DataTable>
                                    </TableContainer>
                                )}
                                {/* Table Footer */}
                                {!loading && !error && (
                                    <div className='py-4 text-xs text-gray-500 italic px-4 bg-white' data-testid='comparison-table-footer'>
                                        *Note: "Telemedicine Credit" indicates a 10% credit was applied
                                        to the specialty
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className='bg-white p-8 rounded-lg shadow-sm flex items-center justify-center h-64 mt-8' data-testid='comparison-table-empty'>
                                <div className='text-center text-gray-500'>
                                    <h4 className='text-lg font-medium mb-2'>
                                        Select Rosters to Compare
                                    </h4>
                                    <p className='text-sm'>
                                        Please select at least 2 rosters from the filters panel to
                                        view the comparison table.
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
});

export default RosterComparisonSummary;
